import { Check, Star } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { trackPurchase } from "@/lib/analytics";

export default function PricingSection({ planId = null, compact = false, onPaid }) {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    api.get("/pricing")
      .then((r) => { if (!cancelled) setPricing(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleBuy = async (tierId) => {
    if (!pricing) return;
    if (!planId) {
      toast.info("Start with your free plan", { description: "Paste your website, get your plan, then pick a package on it." });
      navigate("/generate");
      return;
    }
    if (!pricing.razorpay_enabled) {
      toast.info("Payments coming soon", { description: "Checkout isn't switched on yet. Please try again shortly." });
      return;
    }
    if (!window.Razorpay) {
      toast.error("Checkout couldn't load. Check your connection and try again.");
      return;
    }
    try {
      setLoading(tierId);
      const { data } = await api.post("/payments/create-order", { tier: tierId, plan_id: planId });
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "LeadGen",
        description: data.tier_label,
        order_id: data.order_id,
        notes: { plan_id: planId, tier: tierId },
        theme: { color: "#4f46e5" },
        handler: async (res) => {
          try {
            const { data: paid } = await api.post("/payments/verify", { ...res, plan_id: planId });
            trackPurchase({ orderId: paid.order_id, tierId: paid.tier, tierName: paid.tier_name, amount: paid.amount_inr });
            toast.success("Payment successful! Your plan is unlocked.", { description: "A receipt is on its way to your email." });
            onPaid?.();
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment could not be verified.");
          } finally {
            setLoading(null);
          }
        },
        modal: { ondismiss: () => setLoading(null) },
      });
      rzp.on("payment.failed", (r) => {
        toast.error("Payment failed", { description: r?.error?.description || "Please try again." });
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoading(null);
    }
  };

  if (!pricing) return <div className="py-24 text-center text-zinc-400">Loading pricing…</div>;

  return (
    <section id="pricing" data-testid="pricing-section" className={compact ? "py-16" : "py-24"}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-14">
          <span className="chip"><span className="h-1.5 w-1.5 rounded-full bg-indigo-600" /> Pricing</span>
          <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight text-zinc-900 mt-4">
            Priced for founders, not agencies.
          </h2>
          <p className="text-zinc-600 mt-3">
            Pay once. Get your next-moves dashboard plus all the assets to ship them. Upgrade anytime.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {pricing.tiers.map((t) => (
            <div
              key={t.id}
              data-testid={`pricing-tier-${t.id}`}
              className={`card-hover relative bg-white border rounded-2xl p-6 sm:p-8 ${
                t.highlight ? "border-indigo-600 md:-translate-y-2 shadow-xl shadow-indigo-100" : "border-zinc-200"
              }`}
            >
              {t.highlight && (
                <div className="absolute -top-3 left-8 inline-flex items-center gap-1 bg-indigo-600 text-white text-xs font-medium px-3 py-1 rounded-full">
                  <Star className="h-3 w-3" /> Most popular
                </div>
              )}
              <div className="text-sm uppercase tracking-widest text-zinc-500">{t.name}</div>
              <div className="mt-3 flex flex-wrap items-baseline gap-x-1 gap-y-1">
                <span data-testid={`pricing-tier-${t.id}-price`} className="font-display font-black text-4xl sm:text-5xl text-zinc-900 break-words">₹{t.price_inr.toLocaleString("en-IN")}</span>
                <span className="text-zinc-500">/ one-time</span>
              </div>
              <ul className="mt-6 space-y-3 text-sm text-zinc-700">
                {t.features.map((f) => (
                  <li key={`${t.id}-${f}`} className="flex gap-2">
                    <Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleBuy(t.id)}
                disabled={!!loading}
                data-testid={`pricing-tier-${t.id}-btn`}
                className={`btn-lift mt-8 w-full h-12 rounded-full font-medium text-sm ${
                  t.highlight ? "bg-indigo-600 text-white hover:bg-indigo-700" : "bg-zinc-900 text-white hover:bg-indigo-600"
                }`}
              >
                {loading === t.id ? "Opening checkout…" : t.cta}
              </button>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          {pricing.services.map((s) => (
            <div key={s.id} data-testid={`service-${s.id}`} className="bg-zinc-900 text-white rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest text-indigo-300">Service</div>
                <h3 className="font-display font-black text-2xl mt-2">{s.name}</h3>
                <p className="text-zinc-400 text-sm mt-2 max-w-md">{s.description}</p>
              </div>
              <div className="sm:text-right sm:shrink-0 min-w-0">
                <div className="text-xs text-zinc-500">Starting</div>
                <div data-testid={`service-${s.id}-price`} className="font-display font-black text-xl sm:text-2xl break-words">₹{s.price_range_inr}</div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
